import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ClientRegisteredVendor,
  CompanyVendor,
  ExemptVendor,
  Note,
  Vendor
} from 'src/app/classes/models';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { NoteDisplayModalComponent } from 'src/app/modals/note-display-modal/note-display-modal.component';
import { NoteEmailDisplayModalComponent } from 'src/app/modals/note-email-display-modal/note-email-display-modal.component';
import { NoteTicketDisplayModalComponent } from 'src/app/modals/note-ticket-display-modal/note-ticket-display-modal.component';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-client-my-vendors-history',
  templateUrl: './client-my-vendor-history.component.html',
  styleUrls: ['./client-my-vendor-history.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientMyVendorHistoryComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private CompanyVendorService: CompanyVendorService,
    private ToastService: ToastService,
    private FormBuilder: FormBuilder,
    private ModalService: NgbModal
  ) {
    const showSystemLogsControl =
      this.historyGridControlsForm.get('hideSystemLogs');
    const showAccountingLogsControl =
      this.historyGridControlsForm.get('showAccountingLogs');

    showSystemLogsControl?.valueChanges.subscribe({
      next: () => {
        this.onShowSystemLogs();
        this._cdr.markForCheck();
      }
    });

    showAccountingLogsControl?.valueChanges.subscribe({
      next: () => {
        this.onShowAccountingLogs();
        this._cdr.markForCheck();
      }
    });
  }

  Vendor: Vendor | ExemptVendor | ClientRegisteredVendor | null = null;

  suppliedGridQueryData: GenericObject = {
    communicationType: {
      $and: [{ $ne: 'Accounting Log' }]
    }
  };
  reloadGrid: boolean = false;

  DisplaySystemEmailNoteModel: {
    showSystemEmailNote: boolean;
  } = {
    showSystemEmailNote: false
  };

  historyGridControlsForm: FormGroup = this.FormBuilder.group({
    hideSystemLogs: [false],
    showAccountingLogs: [false]
  });

  // Display these actions in the action column.
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['view']
  };

  action({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: Note;
  }) {
    if (action === 'view') {
      const { id: NoteId, ticketId, EmailId } = rowData;

      // Open a note modal based on the passed in id.
      if (ticketId) {
        const { componentInstance } = this.ModalService.open(
          NoteTicketDisplayModalComponent,
          { backdrop: 'static', size: 'lg' }
        );
        Object.assign(componentInstance, { NoteId });
      } else if (EmailId) {
        const { componentInstance } = this.ModalService.open(
          NoteEmailDisplayModalComponent,
          { backdrop: 'static', size: 'lg' }
        );
        Object.assign(componentInstance, { NoteId });
      } else {
        const { componentInstance } = this.ModalService.open(
          NoteDisplayModalComponent,
          { backdrop: 'static', size: 'lg' }
        );
        Object.assign(componentInstance, { NoteId });
      }
    }
  }

  onShowSystemLogs() {
    const hideSystemLogsControl =
      this.historyGridControlsForm.get('hideSystemLogs');

    if (
      !this.suppliedGridQueryData['communicationType'] ||
      typeof this.suppliedGridQueryData['communicationType'] !== 'object'
    ) {
      this.suppliedGridQueryData['communicationType'] = { $and: [] };
    }

    const andConditions = this.suppliedGridQueryData['communicationType']
      .$and as any[];
    const index = andConditions.findIndex(
      (condition) => condition.$ne === 'System Generated'
    );

    if (hideSystemLogsControl?.value) {
      if (index === -1) {
        andConditions.push({ $ne: 'System Generated' });
      }
    } else {
      if (index > -1) {
        andConditions.splice(index, 1);
      }
    }

    this.reloadGrid = true;
  }

  onShowAccountingLogs() {
    const showAccountingLogsControl =
      this.historyGridControlsForm.get('showAccountingLogs');

    if (
      !this.suppliedGridQueryData['communicationType'] ||
      typeof this.suppliedGridQueryData['communicationType'] !== 'object'
    ) {
      this.suppliedGridQueryData['communicationType'] = { $and: [] };
    }

    const andConditions = this.suppliedGridQueryData['communicationType']
      .$and as any[];
    const index = andConditions.findIndex(
      (condition) => condition.$ne === 'Accounting Log'
    );

    if (showAccountingLogsControl?.value) {
      if (index > -1) {
        andConditions.splice(index, 1);
      }
    } else {
      if (index === -1) {
        andConditions.push({ $ne: 'Accounting Log' });
      }
    }

    this.reloadGrid = true;
  }

  ngOnInit(): void {
    const paramMap: ParamMap = this.route.snapshot.paramMap;
    const VendorId = Number(paramMap.get('VendorId')) || null;
    const ExemptVendorId = Number(paramMap.get('ExemptVendorId')) || null;
    const ClientRegisteredVendorId =
      Number(paramMap.get('ClientRegisteredVendorId')) || null;
    const companyVendorQuery = {
      attributes: JSON.stringify(['legalName'])
    };

    Object.assign(this.suppliedGridQueryData, {
      attributes: ['ticketId', 'EmailId'],
      order: [['createdAt', 'DESC']]
    });

    if (VendorId) {
      Object.assign(this.suppliedGridQueryData, {
        $or: [
          { VendorId },
          { $and: [{ modelTable: 'Vendor' }, { modelId: VendorId }] }
        ]
      });
      Object.assign(companyVendorQuery, {
        VendorId,
        Vendor: JSON.stringify({})
      });
    }

    if (ExemptVendorId) {
      Object.assign(this.suppliedGridQueryData, {
        $or: [
          { ExemptVendorId },
          {
            $and: [{ modelTable: 'ExemptVendor' }, { modelId: ExemptVendorId }]
          }
        ]
      });
      Object.assign(companyVendorQuery, {
        ExemptVendorId,
        ExemptVendor: JSON.stringify({})
      });
    }

    if (ClientRegisteredVendorId) {
      Object.assign(this.suppliedGridQueryData, {
        $or: [
          { ClientRegisteredVendorId },
          {
            $and: [
              { modelTable: 'ClientRegisteredVendor' },
              { modelId: ClientRegisteredVendorId }
            ]
          }
        ]
      });
      Object.assign(companyVendorQuery, {
        ClientRegisteredVendorId,
        ClientRegisteredVendor: JSON.stringify({})
      });
    }

    this.CompanyVendorService.getCompanyVendor(companyVendorQuery).subscribe({
      next: (CompanyVendor: CompanyVendor) => {
        if (!CompanyVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No CompanyVendor Record found.',
            { returnError: true }
          );
        }

        const { Vendor, ExemptVendor, ClientRegisteredVendor } = CompanyVendor;

        if (Vendor) {
          this.Vendor = Vendor;
        }

        if (ExemptVendor) {
          this.Vendor = ExemptVendor;
        }

        if (ClientRegisteredVendor) {
          this.Vendor = ClientRegisteredVendor;
        }
        this._cdr.markForCheck();
      }
    });
  }
}
